import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Calendar, CheckCircle, Clock, AlertCircle, Search, Filter, RefreshCw, Users, FileSpreadsheet, BarChart2, ShieldAlert, Edit3, Info } from "lucide-react";
import { motion } from "framer-motion";
import Swal from "sweetalert2";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white border border-gray-200 shadow-xl rounded-xl p-4 text-sm">
        <p className="font-bold text-gray-900 mb-2">{label}</p>
        {payload.map((entry, index) => (
          <div key={index} className="flex items-center gap-2 mb-1">
            <span className="inline-block w-3 h-3 rounded-full" style={{ backgroundColor: entry.fill || entry.color }} />
            <span className="text-gray-600">{entry.name}:</span>
            <span className="font-bold text-gray-900">{entry.value}</span>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

const ManageAttendance = () => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All");
  const [chartData, setChartData] = useState([]);
  const [chartLoading, setChartLoading] = useState(false);

  const todayStr = new Date().toISOString().split("T")[0];
  const [startDate, setStartDate] = useState(todayStr);
  const [endDate, setEndDate] = useState(todayStr);

  const allRoles = ["CEO", "Manager", "Team Lead", "Developer", "Designer", "Employee", "Intern"];

  const fetchAttendance = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("portalToken") || localStorage.getItem("admin");
      let queryParams = [];
      if (startDate) queryParams.push(`startDate=${startDate}`);
      if (endDate) queryParams.push(`endDate=${endDate}`);
      if (roleFilter !== "All") queryParams.push(`role=${roleFilter}`);
      if (statusFilter !== "All") queryParams.push(`status=${statusFilter}`);
      if (searchTerm) queryParams.push(`search=${searchTerm}`);
      const queryString = queryParams.length > 0 ? `?${queryParams.join("&")}` : "";
      const res = await axios.get(`${window.API_URL}/api/portal/admin/attendance${queryString}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRecords(res.data);
    } catch (err) {
      console.error(err);
      Swal.fire({ icon: "error", title: "Error", text: "Failed to fetch attendance logs" });
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, roleFilter, statusFilter, searchTerm]);

  const fetchChartStats = useCallback(async () => {
    setChartLoading(true);
    try {
      const token = localStorage.getItem("portalToken") || localStorage.getItem("admin");
      let params = [];
      if (startDate) params.push(`startDate=${startDate}`);
      if (endDate) params.push(`endDate=${endDate}`);
      const queryString = params.length > 0 ? `?${params.join("&")}` : "";
      const res = await axios.get(`${window.API_URL}/api/portal/admin/attendance/stats${queryString}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const formatted = res.data.map((d) => ({
        ...d,
        dateLabel: new Date(d.date + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" }),
      }));
      setChartData(formatted);
    } catch (err) {
      console.error("Failed to load chart data", err);
    } finally {
      setChartLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    fetchAttendance();
    fetchChartStats();
  }, [fetchAttendance, fetchChartStats]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    fetchAttendance();
  };

  const handleQuickDateFilter = (type) => {
    const today = new Date();
    const todayStr = today.toISOString().split("T")[0];
    if (type === "today") {
      setStartDate(todayStr); setEndDate(todayStr);
    } else if (type === "yesterday") {
      const yesterday = new Date(); yesterday.setDate(today.getDate() - 1);
      const s = yesterday.toISOString().split("T")[0]; setStartDate(s); setEndDate(s);
    } else if (type === "week") {
      const weekAgo = new Date(); weekAgo.setDate(today.getDate() - 7);
      setStartDate(weekAgo.toISOString().split("T")[0]); setEndDate(todayStr);
    } else if (type === "month") {
      const monthAgo = new Date(); monthAgo.setDate(today.getDate() - 30);
      setStartDate(monthAgo.toISOString().split("T")[0]); setEndDate(todayStr);
    }
  };

  const totalPresent = records.filter((r) => r.status === "Present" || !r.status).length;
  const totalLate = records.filter((r) => r.status === "Late").length;
  const totalAbsent = records.filter((r) => r.status === "Absent").length;
  const totalRecords = records.length;
  const attendanceRate = totalRecords > 0 ? Math.round(((totalPresent + totalLate) / totalRecords) * 100) : 100;
  const isMultiDay = chartData.length > 1;

  const handleExportCSV = () => {
    if (records.length === 0) { Swal.fire("No Records", "There is no data to export.", "info"); return; }
    const headers = ["Name,Role,Email,Department,Date,Time,Status,Source,OverrideReason\n"];
    const rows = records.map((r) => {
      const dateObj = new Date(r.date);
      const formattedDate = dateObj.toLocaleDateString();
      const formattedTime = dateObj.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
      return `"${r.user?.fullName || ""}","${r.user?.role || ""}","${r.user?.email || ""}","${r.user?.fieldOrDepartment || ""}","${formattedDate}","${formattedTime}","${r.status || "Present"}","${r.source || "manual"}","${(r.overrideReason || "").replace(/"/g, '""')}"`;
    });
    const csvContent = "data:text/csv;charset=utf-8," + headers.concat(rows.join("\n")).join("");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `attendance_report_${startDate}_to_${endDate}.csv`);
    document.body.appendChild(link); link.click(); document.body.removeChild(link);
  };

  const handleOverrideAttendance = async (record) => {
    const defaultDate = new Date(record.date).toISOString().split('T')[0];
    const { value: formValues } = await Swal.fire({
      title: `<span style="font-size:18px;font-weight:700;">Override Attendance</span>`,
      html: `
        <div style="text-align: left; font-size: 13px; color: #374151;">
          <p style="margin-bottom: 10px;"><strong>User:</strong> ${record.user?.fullName || 'N/A'} (${record.user?.email || ''})</p>
          <label style="display:block; font-weight:600; margin-bottom: 4px;">Date</label>
          <input id="swal-override-date" type="date" value="${defaultDate}" class="swal2-input" style="width: 100%; margin: 0 0 12px 0; font-size: 14px; height: 38px;" />
          
          <label style="display:block; font-weight:600; margin-bottom: 4px;">Status</label>
          <select id="swal-override-status" class="swal2-input" style="width: 100%; margin: 0 0 12px 0; font-size: 14px; height: 38px;">
            <option value="Present" ${record.status === 'Present' ? 'selected' : ''}>Present</option>
            <option value="Late" ${record.status === 'Late' ? 'selected' : ''}>Late</option>
            <option value="Absent" ${record.status === 'Absent' ? 'selected' : ''}>Absent</option>
            <option value="Excused" ${record.status === 'Excused' ? 'selected' : ''}>Excused</option>
          </select>

          <label style="display:block; font-weight:600; margin-bottom: 4px;">Audit Reason (Required) *</label>
          <textarea id="swal-override-reason" placeholder="State reason (e.g. medical leave approved, electricity outage, admin exception)..." class="swal2-textarea" style="width: 100%; margin: 0; min-height: 70px; font-size: 13px;"></textarea>
        </div>
      `,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: 'Save Override',
      confirmButtonColor: '#f06600',
      preConfirm: () => {
        const date = document.getElementById('swal-override-date').value;
        const status = document.getElementById('swal-override-status').value;
        const reason = document.getElementById('swal-override-reason').value;
        if (!reason || !reason.trim()) {
          Swal.showValidationMessage('A reason is mandatory for an attendance override audit trail.');
          return false;
        }
        return { date, status, reason };
      }
    });

    if (!formValues) return;

    try {
      const token = localStorage.getItem('portalToken') || localStorage.getItem('admin');
      await axios.put(`${window.API_URL}/api/portal/admin/attendance/override`, {
        userId: record.user?._id,
        date: formValues.date,
        status: formValues.status,
        reason: formValues.reason
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      Swal.fire({
        icon: 'success',
        title: 'Attendance Overridden',
        text: `Updated to ${formValues.status}.`,
        toast: true,
        position: 'top-end',
        timer: 2500,
        showConfirmButton: false
      });
      fetchAttendance();
      fetchChartStats();
    } catch (err) {
      Swal.fire('Error', err.response?.data?.message || 'Failed to override attendance.', 'error');
    }
  };

  return (
    <div className="space-y-6 text-gray-900 pb-12">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
        <div>
          <h2 className="text-3xl font-extrabold text-gray-900 tracking-tight">Attendance Log Manager</h2>
          <p className="text-gray-600 mt-1 text-sm">Monitor, track, and filter daily attendance logs for interns and employees.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={handleExportCSV} className="flex items-center bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-xl transition-all font-semibold text-sm shadow-md hover:shadow-lg">
            <FileSpreadsheet className="w-4 h-4 mr-2" /> Export CSV
          </button>
          <button onClick={() => { fetchAttendance(); fetchChartStats(); }} className="p-2 border border-gray-300 rounded-xl bg-white hover:bg-gray-100 text-gray-700 transition-colors shadow-sm" title="Refresh list">
            <RefreshCw className={`w-5 h-5 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {/* KPI Panels */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        <motion.div whileHover={{ y: -3 }} className="bg-white border border-gray-200 p-6 rounded-2xl shadow-sm flex items-center gap-4">
          <div className="p-3 bg-green-500/10 text-green-600 rounded-xl"><CheckCircle className="w-8 h-8" /></div>
          <div><span className="text-xs font-semibold uppercase tracking-wider text-gray-600">Present</span><h3 className="text-2xl font-bold text-gray-900 mt-1">{totalPresent}</h3></div>
        </motion.div>
        <motion.div whileHover={{ y: -3 }} className="bg-white border border-gray-200 p-6 rounded-2xl shadow-sm flex items-center gap-4">
          <div className="p-3 bg-yellow-500/10 text-yellow-600 rounded-xl"><Clock className="w-8 h-8" /></div>
          <div><span className="text-xs font-semibold uppercase tracking-wider text-gray-600">Late</span><h3 className="text-2xl font-bold text-gray-900 mt-1">{totalLate}</h3></div>
        </motion.div>
        <motion.div whileHover={{ y: -3 }} className="bg-white border border-gray-200 p-6 rounded-2xl shadow-sm flex items-center gap-4">
          <div className="p-3 bg-red-500/10 text-red-600 rounded-xl"><AlertCircle className="w-8 h-8" /></div>
          <div><span className="text-xs font-semibold uppercase tracking-wider text-gray-600">Absent</span><h3 className="text-2xl font-bold text-gray-900 mt-1">{totalAbsent}</h3></div>
        </motion.div>
        <motion.div whileHover={{ y: -3 }} className="bg-white border border-gray-200 p-6 rounded-2xl shadow-sm flex items-center gap-4">
          <div className="p-3 bg-blue-500/10 text-blue-600 rounded-xl"><Users className="w-8 h-8" /></div>
          <div><span className="text-xs font-semibold uppercase tracking-wider text-gray-600">Attendance Rate</span><h3 className="text-2xl font-bold text-gray-900 mt-1">{attendanceRate}%</h3></div>
        </motion.div>
      </div>

      {/* ===== ATTENDANCE TREND CHART ===== */}
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="bg-white border border-gray-200 rounded-2xl shadow-sm p-6">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-[#f06600]/10 rounded-lg"><BarChart2 className="w-5 h-5 text-[#f06600]" /></div>
            <div>
              <h3 className="text-base font-bold text-gray-900">Daily Attendance Trend</h3>
              <p className="text-xs text-gray-500 mt-0.5">
                {isMultiDay ? "Present vs. Absent/Missing across the selected date range" : "Attendance breakdown for the selected day"}
              </p>
            </div>
          </div>
          {chartLoading && <RefreshCw className="w-4 h-4 animate-spin text-[#f06600]" />}
        </div>

        {chartData.length === 0 && !chartLoading ? (
          <div className="flex flex-col items-center justify-center py-14 text-gray-400">
            <BarChart2 className="w-10 h-10 mb-3 opacity-25" />
            <p className="text-sm font-medium">No data available for the selected range.</p>
            <p className="text-xs mt-1">Try a wider date range to see trend data.</p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={chartData} margin={{ top: 4, right: 16, left: -10, bottom: 4 }} barCategoryGap="30%" barGap={4}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
              <XAxis dataKey="dateLabel" tick={{ fontSize: 11, fill: "#6b7280", fontWeight: 500 }} axisLine={false} tickLine={false} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#6b7280" }} axisLine={false} tickLine={false} />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: "rgba(240,102,0,0.05)" }} />
              <Legend wrapperStyle={{ fontSize: "12px", fontWeight: 600, paddingTop: "12px" }} iconType="circle" iconSize={10} />
              <Bar dataKey="Present" name="Present" fill="#22c55e" radius={[5, 5, 0, 0]} maxBarSize={48} />
              <Bar dataKey="Late" name="Late" fill="#f59e0b" radius={[5, 5, 0, 0]} maxBarSize={48} />
              <Bar dataKey="Absent" name="Absent / Missing" fill="#ef4444" radius={[5, 5, 0, 0]} maxBarSize={48} />
            </BarChart>
          </ResponsiveContainer>
        )}

        {/* Legend Callout */}
        <div className="flex flex-wrap gap-4 mt-4 pt-4 border-t border-gray-100 text-xs text-gray-600">
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-green-500 inline-block" /><strong>Present</strong> — Marked attendance on time</span>
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-amber-400 inline-block" /><strong>Late</strong> — Marked after cutoff</span>
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-red-500 inline-block" /><strong>Absent / Missing</strong> — No attendance record (includes untracked)</span>
        </div>
      </motion.div>

      {/* Advanced Filters */}
      <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm space-y-4">
        <h3 className="text-base font-bold text-gray-900 flex items-center">
          <Filter className="w-4 h-4 mr-2 text-[#f06600]" /> Filters &amp; Search
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <form onSubmit={handleSearchSubmit} className="relative">
            <input type="text" placeholder="Search user name/email..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full bg-gray-50 border border-gray-200 rounded-xl pl-10 pr-4 py-2 text-sm focus:ring-2 focus:ring-[#f06600] outline-none transition-all text-gray-900 placeholder-gray-600" />
            <Search className="absolute left-3.5 top-2.5 w-4 h-4 text-gray-600" />
          </form>
          <div>
            <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-2 text-sm focus:ring-2 focus:ring-[#f06600] outline-none text-gray-900 font-medium">
              <option value="All">All Roles</option>
              {allRoles.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-2 text-sm focus:ring-2 focus:ring-[#f06600] outline-none text-gray-900 font-medium">
              <option value="All">All Statuses</option>
              <option value="Present">Present</option>
              <option value="Late">Late</option>
              <option value="Absent">Absent</option>
            </select>
          </div>
          <div className="flex gap-2 items-center">
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-1.5 text-xs focus:ring-2 focus:ring-[#f06600] outline-none text-gray-900" title="Start Date" />
            <span className="text-gray-600 font-semibold text-xs">to</span>
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-1.5 text-xs focus:ring-2 focus:ring-[#f06600] outline-none text-gray-900" title="End Date" />
          </div>
        </div>
        <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-100">
          <span className="text-xs text-gray-600 self-center font-medium mr-2">Quick Dates:</span>
          <button onClick={() => handleQuickDateFilter("today")} className={`px-3 py-1 rounded-lg text-xs font-semibold transition-all ${startDate === todayStr && endDate === todayStr ? "bg-[#f06600] text-gray-900" : "bg-gray-100 hover:bg-gray-200 text-gray-700"}`}>Today</button>
          <button onClick={() => handleQuickDateFilter("yesterday")} className="px-3 py-1 rounded-lg text-xs font-semibold bg-gray-100 hover:bg-gray-200 text-gray-700 transition-all">Yesterday</button>
          <button onClick={() => handleQuickDateFilter("week")} className="px-3 py-1 rounded-lg text-xs font-semibold bg-gray-100 hover:bg-gray-200 text-gray-700 transition-all">Last 7 Days</button>
          <button onClick={() => handleQuickDateFilter("month")} className="px-3 py-1 rounded-lg text-xs font-semibold bg-gray-100 hover:bg-gray-200 text-gray-700 transition-all">Last 30 Days</button>
        </div>
      </div>

      {/* Logs Table */}
      <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200 text-gray-600 text-xs font-bold uppercase tracking-wider">
                <th className="p-4">Name</th>
                <th className="p-4">Role & Dept</th>
                <th className="p-4">Date & Time</th>
                <th className="p-4 text-center">Status</th>
                <th className="p-4">Source & Audit</th>
                <th className="p-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {records.map((record) => {
                const dateObj = new Date(record.date);
                const formattedDate = dateObj.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric", year: "numeric" });
                const formattedTime = dateObj.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
                const isOverridden = record.source === "admin_override";
                const isAuto = record.source === "auto";

                return (
                  <tr key={record._id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="p-4">
                      <div className="font-semibold text-gray-900">{record.user?.fullName || "N/A"}</div>
                      <div className="text-xs text-gray-500">{record.user?.email || ""}</div>
                    </td>
                    <td className="p-4">
                      <div className="flex flex-col gap-1 items-start">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${record.user?.role === "Intern" ? "bg-orange-500/10 text-[#f06600]" : "bg-blue-500/10 text-blue-600"}`}>{record.user?.role || "N/A"}</span>
                        <span className="text-xs text-gray-500">{record.user?.fieldOrDepartment || "—"}</span>
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="text-sm text-gray-800 font-medium">{formattedDate}</div>
                      <div className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                        <Clock className="w-3 h-3 text-gray-400" />
                        {formattedTime}
                      </div>
                    </td>
                    <td className="p-4 text-center">
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold ${
                        record.status === "Present" || !record.status
                          ? "bg-green-100 text-green-700 border border-green-200"
                          : record.status === "Late"
                          ? "bg-yellow-100 text-yellow-700 border border-yellow-200"
                          : record.status === "Excused"
                          ? "bg-blue-100 text-blue-700 border border-blue-200"
                          : "bg-red-100 text-red-700 border border-red-200"
                      }`}>
                        {record.status || "Present"}
                      </span>
                    </td>
                    <td className="p-4">
                      {isOverridden ? (
                        <div className="flex flex-col gap-0.5">
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-bold bg-amber-50 text-amber-800 border border-amber-200 w-fit">
                            <ShieldAlert className="w-3 h-3 text-amber-600" /> Admin Override
                          </span>
                          {record.overrideReason && (
                            <span className="text-xs text-gray-500 line-clamp-1 italic" title={record.overrideReason}>
                              "{record.overrideReason}"
                            </span>
                          )}
                        </div>
                      ) : isAuto ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold bg-blue-50 text-blue-700 border border-blue-200 w-fit">
                          Auto (Tasks)
                        </span>
                      ) : (
                        <span className="text-xs text-gray-500 font-medium">
                          Manual Self-mark
                        </span>
                      )}
                    </td>
                    <td className="p-4 text-right">
                      <button
                        onClick={() => handleOverrideAttendance(record)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-xl text-xs font-bold text-gray-700 hover:text-[#f06600] bg-gray-50 hover:bg-[#f06600]/10 border border-gray-200 hover:border-[#f06600]/40 transition-all shadow-sm"
                        title="Override this user's attendance with audit reason"
                      >
                        <Edit3 className="w-3.5 h-3.5" /> Override
                      </button>
                    </td>
                  </tr>
                );
              })}
              {records.length === 0 && !loading && (
                <tr><td colSpan="6" className="p-12 text-center text-gray-500">
                  <div className="flex flex-col items-center justify-center space-y-2">
                    <Calendar className="w-10 h-10 text-gray-600" />
                    <p className="font-bold text-gray-700">No attendance logs found</p>
                    <p className="text-xs text-gray-600">Try adjusting the filter criteria or date range.</p>
                  </div>
                </td></tr>
              )}
              {loading && (
                <tr><td colSpan="6" className="p-12 text-center text-gray-600">
                  <div className="flex items-center justify-center gap-2">
                    <RefreshCw className="w-5 h-5 animate-spin text-[#f06600]" />
                    <span className="font-semibold text-sm">Loading attendance records...</span>
                  </div>
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ManageAttendance;
